import React from 'react'
import styled from 'styled-components'
import { ReactComponent as MenuDots } from '../../Assets/icons/menuDots.svg'
import { ReactComponent as ShieldAdmin } from '../../Assets/icons/shieldAdmin.svg'
import { ReactComponent as ProIcon } from "../../Assets/iconsv2/generic/PremiumUser.svg";
import { ReactComponent as PlatformIcon } from "../../Assets/iconsv2/generic/BusinessUser.svg";
import { ReactComponent as PlatformIconLight } from "../../Assets/iconsv2/generic/BusinessUserLight.svg";
import { Item, Menu, useContextMenu } from 'react-contexify'
import { useTranslate } from '@tolgee/react';
import { displayUserAvatar } from '../../Helpers/chatHelper/AvatarHelper'
import { useGenericComponentsStateService } from '../../Context/GenericComponentsStateContext';
import { useCallsService } from '../../Context/CallsContext';
import { useAuthService } from '../../Context/AuthContext';
import 'react-contexify/dist/ReactContexify.css';

const CurrentParticipant = ({ participant, online, callOfflineParticipant, meetingParticipant, callInfo, refreshCallDetails, isConferenceCall }) => {
  const { t } = useTranslate();
  const { theme } = useGenericComponentsStateService();
  const { kickParticipant, muteParticipant, makeCallAdmin, revokeCallAdmin } = useCallsService();
  const { user } = useAuthService();

  const MENU_ID = `participant-menu-${participant.id}`;
  const { show } = useContextMenu({ id: MENU_ID });

  const isCurrentUserAdmin = callInfo?.call_admin_ids?.includes(user.id);
  const isParticipantAdmin = callInfo?.call_admin_ids?.includes(participant.id);

  const displayMenu = (e) => {
    e.preventDefault();
    show({ event: e });
  };

  const handleKick = () => {
    if (window.confirm(t('call_modal.confirm_kick'))) {
      kickParticipant(
        callInfo.id,
        participant.id,
        () => {
          refreshCallDetails && refreshCallDetails();
        },
        (error) => {
          console.error('Failed to kick participant:', error);
        }
      );
    }
  };

  const handleMute = () => {
    muteParticipant(
      callInfo.id,
      participant.id,
      () => {
        refreshCallDetails && refreshCallDetails();
      },
      (error) => {
        console.error('Failed to mute participant:', error);
      }
    );
  };

  const handleToggleAdmin = () => {
    const confirmMessage = isParticipantAdmin
      ? t('call_modal.confirm_revoke_admin')
      : t('call_modal.confirm_make_admin');

    if (window.confirm(confirmMessage)) {
      const action = isParticipantAdmin ? revokeCallAdmin : makeCallAdmin;
      action(
        callInfo.id,
        participant.id,
        () => {
          refreshCallDetails && refreshCallDetails();
        },
        (error) => {
          console.error('Failed to toggle admin status:', error);
        }
      );
    }
  };

  const parseStatus = () => {
    if (online) {
      return t('general.online')
    } else {
      return t('general.offline')
    }
  }

  return (
    <>
      <Container>
        <Avatar>
          {displayUserAvatar(participant)}
          <ProBadgeWrapper>
            {participant.is_pro_from_platform ? (
              theme === "light" ? (
                <PlatformIconLight />
              ) : (
                <PlatformIcon />
              )
            ) : (
              participant.is_pro && <ProIcon />
            )}
          </ProBadgeWrapper>
        </Avatar>
        <ContactNameWrapper>
          <Name>
            {participant.name}
            {isParticipantAdmin && (
              <AdminBadge title={t('call_modal.admin')}>
                <ShieldAdmin />
              </AdminBadge>
            )}
          </Name>
          <Status offline={!online}>
            {parseStatus()}
          </Status>
        </ContactNameWrapper>

        {!isConferenceCall && participant.id !== user.id && (!online || isCurrentUserAdmin) && (
          <MenuButton onClick={displayMenu}>
            <MenuDots />
          </MenuButton>
        )}
      </Container>

      {!isConferenceCall && participant.id !== user.id && (!online || isCurrentUserAdmin) && (
        <StyledMenu id={MENU_ID}>
          {!online && (
            <Item onClick={() => callOfflineParticipant(participant.id)}>
              <MenuItemContent>
                <span>{t('call_modal.ring')}</span>
              </MenuItemContent>
            </Item>
          )}

          {isCurrentUserAdmin && online && (
            <Item onClick={handleMute}>
              <MenuItemContent>
                <span>{t('call_modal.mute_user')}</span>
              </MenuItemContent>
            </Item>
          )}

          {isCurrentUserAdmin && (
            <Item onClick={handleKick}>
              <MenuItemContent danger>
                <span>{t('call_modal.remove_user')}</span>
              </MenuItemContent>
            </Item>
          )}

          {isCurrentUserAdmin && (
            <Item onClick={handleToggleAdmin}>
              <MenuItemContent>
                <span>{isParticipantAdmin ? t('call_modal.revoke_admin') : t('call_modal.make_admin')}</span>
              </MenuItemContent>
            </Item>
          )}
        </StyledMenu>
      )}
    </>
  )
}

export default CurrentParticipant

const Container = styled.div`
display:flex;
align-items:center;
gap:10px;
padding: 0 16px;
height:45px;
min-height:45px;
max-height:45px;
  &:hover {
    background: ${({ theme }) => theme.call.callElementSelectedBackground};
  }
`
const Avatar = styled.div`
 display: flex;
  height: 42px;
  width: 42px;
  max-width: 42px;
  min-height: 42px;
  min-width: 42px;
  max-height: 42px;
  border-radius: 100%;
  position: relative;
`

const ContactNameWrapper = styled.div`
display:flex;
align-items:center;
width:100%;
overflow:hidden;
gap:10px;
height:100%;
`

const Name = styled.p`
overflow:hidden;
text-overflow:ellipsis;
white-space:nowrap;
font-size: 16px;
font-weight: 400;
line-height: 20px;
color:${({ theme }) => theme.menu.primaryTextColor};
`

const Status = styled.p`
font-size: 12px;
font-weight: 500;
line-height: 20px;
text-align: left;
color:${({ theme, offline }) => offline ? theme.menu.primaryTextColor : theme.primaryButton.color};
margin-left:auto;
display:flex;
gap:5px;
`


const RingButton = styled.button`
display:flex;
align-items:center;
justify-content:center;
border:none;
background:transparent;
cursor:pointer;
svg{
max-height:20px;
max-width:20px;
color:${({ theme }) => theme.primaryButton.color};
}
`

const ProBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 20px;
  height: 16px;
  svg {
    color: ${({ theme }) => theme.loginButton.background};
  }
`;

const MenuButton = styled.button`
  margin-left: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }

  &:hover {
    opacity: 0.7;
  }
`;

const StyledMenu = styled(Menu)`
  overflow: hidden;
  background: transparent;
  color: white;
  padding: 0;
  border-radius: 12px;

  .contexify_item {
    background: ${({ theme }) => theme.menu.groupContentBackground};
    border-bottom: 0.5px solid rgba(255, 255, 255, 0.1);

    &:last-child {
      border-bottom: none;
      border-radius: 0 0 12px 12px;
    }
  }

  .contexify_item:not(.contexify_item-disabled):hover > .contexify_itemContent,
  .contexify_item:not(.contexify_item-disabled):focus > .contexify_itemContent {
    color: inherit;
    background: ${({ theme }) => theme.chat.selectedBackground};
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  color: ${({ theme, danger }) => danger ? '#ff4444' : theme.menu.primaryTextColor};
  padding: 6px 10px;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme, danger }) => danger ? '#ff4444' : theme.menu.primaryTextColor};
  }
`;

const AdminBadge = styled.span`
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.primaryButton.color};
  }
`;